import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { printDoc, esc } from "@/lib/print";
import { Plus, Pencil, Users2, History, Printer, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { CorporateMembersDialog } from "@/components/CorporateMembersDialog";

const empty = { type: "cash", name: "", phone: "", email: "", address: "", insurance_scheme_id: "", scheme_member_no: "", credit_limit: 0, is_active: true };

export default function Customers() {
  const { can } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [schemeOpen, setSchemeOpen] = useState(false);
  const [scheme, setScheme] = useState({ id: null, name: "", type: "private", notes: "", markup_percent: 20 });

  const load = () => api.get("/customers", { params: { type: type === "all" ? undefined : type } }).then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, [type]); // eslint-disable-line
  useEffect(() => { api.get("/insurance-schemes").then((r) => setSchemes(r.data)); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c, insurance_scheme_id: c.insurance_scheme_id || "" }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    try {
      const payload = { ...form, credit_limit: Number(form.credit_limit) || 0, insurance_scheme_id: form.insurance_scheme_id || null };
      if (editId) await api.put(`/customers/${editId}`, payload); else await api.post("/customers", payload);
      toast.success("Customer saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openNewScheme = () => { setScheme({ id: null, name: "", type: "private", notes: "", markup_percent: 20 }); setSchemeOpen(true); };
  const openEditScheme = (s) => { setScheme({ id: s.id, name: s.name, type: s.type, notes: s.notes || "", markup_percent: s.markup_percent ?? 20 }); setSchemeOpen(true); };
  const saveScheme = async () => {
    try {
      const payload = { name: scheme.name, type: scheme.type, notes: scheme.notes, markup_percent: Number(scheme.markup_percent) || 0 };
      if (scheme.id) await api.put(`/insurance-schemes/${scheme.id}`, payload);
      else await api.post("/insurance-schemes", payload);
      toast.success("Scheme saved"); setSchemeOpen(false);
      api.get("/insurance-schemes").then((r) => setSchemes(r.data));
    } catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const [histFor, setHistFor] = useState(null);
  const [membersFor, setMembersFor] = useState(null);
  const [histOrders, setHistOrders] = useState([]);
  const openHistory = async (c) => {
    setHistFor(c); setHistOrders([]);
    try { const { data } = await api.get("/orders", { params: { customer_id: c.id, limit: 300 } }); setHistOrders(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const printInvoice = (o) => {
    const rows = (o.lines || []).map((l) => `<tr><td>${esc(l.generic_name || l.name)}</td><td class="r">${l.quantity}</td><td class="r">${fmtGHS(l.unit_price)}</td><td class="r">${fmtGHS(l.line_total)}</td></tr>`).join("");
    const body = `<div class="row"><div><h1>MobiMed Pharmacy</h1><h2>Invoice ${o.invoice_no}</h2><div class="muted">${esc(o.branch_name)}</div></div>
      <div class="muted" style="text-align:right">${fmtDateTime(o.created_at)}<br/>${esc(o.customer_name)}${o.corporate_member_name ? `<br/>Member: ${esc(o.corporate_member_name)}${o.corporate_member_staff_no ? ` (${esc(o.corporate_member_staff_no)})` : ""}` : ""}<br/>${o.customer_type || ""} customer</div></div>
      <table><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Total</th></tr></thead><tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3" class="r">Total</td><td class="r">${fmtGHS(o.total)}</td></tr></tfoot></table>`;
    printDoc(`Invoice ${o.invoice_no}`, body);
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Cash, corporate credit and insurance customers"
        actions={can("customers.manage") && (
          <div className="flex gap-2">
            {can("schemes.manage") && <Button variant="outline" data-testid="add-scheme-btn" onClick={openNewScheme}>Add Scheme</Button>}
            <Button data-testid="add-customer-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Customer</Button>
          </div>
        )} />

      {schemes.length > 0 && (
        <Card className="p-4 shadow-sm mb-4">
          <h3 className="font-display font-semibold text-foreground mb-2">Insurance schemes &amp; markups</h3>
          <div className="flex flex-wrap gap-2">
            {schemes.map((s) => (
              <div key={s.id} className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs">
                <span className="font-medium text-foreground">{s.name}</span>
                <span className="font-mono" style={{ color: "var(--mm-action)" }}>{s.markup_percent ?? 0}%</span>
                {can("schemes.manage") && (
                  <button data-testid={`edit-scheme-${s.id}`} onClick={() => openEditScheme(s)} className="text-muted-foreground hover:text-foreground"><Pencil className="h-3 w-3" /></button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs value={type} onValueChange={setType}>
        <TabsList>
          <TabsTrigger value="all" data-testid="cust-tab-all">All</TabsTrigger>
          <TabsTrigger value="cash" data-testid="cust-tab-cash">Cash</TabsTrigger>
          <TabsTrigger value="corporate" data-testid="cust-tab-corporate">Corporate</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="cust-tab-insurance">Insurance</TabsTrigger>
        </TabsList>
        <TabsContent value={type} className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Name</th><th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3">Contact</th>
                <th className="py-2.5 px-3">Scheme / Member</th><th className="py-2.5 px-3 text-right">Credit Limit</th><th className="py-2.5 px-3 text-right">Balance</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {customers.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><Users2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No customers</td></tr>}
                {customers.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium text-foreground">{c.name}{c.type === "corporate" && (c.members || []).length > 0 && <div className="text-[11px] font-normal text-muted-foreground">{c.members.filter((m) => m.is_active ?? true).length} member(s)</div>}</td>
                    <td className="py-2 px-3"><StatusBadge value={c.type} /></td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{c.phone ? <a href={`tel:${c.phone}`} className="text-sky-600 hover:underline" data-testid={`call-customer-${c.id}`}>{c.phone}</a> : "—"}<br />{c.email}</td>
                    <td className="py-2 px-3 text-xs">{c.scheme_name || "—"}{c.scheme_member_no && <div className="font-mono text-muted-foreground">{c.scheme_member_no}</div>}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{c.type === "corporate" ? fmtGHS(c.credit_limit) : "—"}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">{c.type === "corporate" ? fmtGHS(c.balance) : "—"}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {c.type === "corporate" && <Button variant="ghost" size="sm" title="Members" data-testid={`members-customer-${c.id}`} onClick={() => setMembersFor(c)}><UsersRound className="h-3.5 w-3.5" /></Button>}
                      <Button variant="ghost" size="sm" data-testid={`history-customer-${c.id}`} onClick={() => openHistory(c)}><History className="h-3.5 w-3.5" /></Button>
                      {can("customers.manage") && <Button variant="ghost" size="sm" data-testid={`edit-customer-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-3.5 w-3.5" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={set("type")}>
                <SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="corporate">Corporate</SelectItem><SelectItem value="insurance">Insurance</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input data-testid="customer-name-input" value={form.name} onChange={set("name")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone <span className="text-rose-500">*</span></Label><Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} placeholder="Required" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
            {form.type === "insurance" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Scheme</Label>
                  <Select value={form.insurance_scheme_id} onValueChange={set("insurance_scheme_id")}>
                    <SelectTrigger data-testid="customer-scheme-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Member No.</Label><Input value={form.scheme_member_no} onChange={set("scheme_member_no")} /></div>
              </div>
            )}
            {form.type === "corporate" && <div><Label>Credit limit (GHS)</Label><Input type="number" data-testid="customer-credit-input" value={form.credit_limit} onChange={set("credit_limit")} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-customer-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={schemeOpen} onOpenChange={setSchemeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{scheme.id ? "Edit Insurance Scheme" : "New Insurance Scheme"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="scheme-name-input" value={scheme.name} onChange={(e) => setScheme((s) => ({ ...s, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={scheme.type} onValueChange={(v) => setScheme((s) => ({ ...s, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="nhis">NHIS</SelectItem><SelectItem value="private">Private / HMO</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Markup (%)</Label><Input type="number" step="0.1" data-testid="scheme-markup-input" value={scheme.markup_percent} onChange={(e) => setScheme((s) => ({ ...s, markup_percent: e.target.value }))} /></div>
            </div>
            <div><Label>Notes</Label><Input value={scheme.notes} onChange={(e) => setScheme((s) => ({ ...s, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSchemeOpen(false)}>Cancel</Button><Button data-testid="save-scheme-btn" onClick={saveScheme}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CorporateMembersDialog key={membersFor?.id} company={membersFor} onClose={() => setMembersFor(null)} onChanged={load} canManage={can("customers.manage")} />

      <Dialog open={!!histFor} onOpenChange={(o) => !o && setHistFor(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Order history — {histFor?.name}</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Invoice</th><th className="py-2">Date</th><th className="py-2">Member</th><th className="py-2">Branch</th><th className="py-2 text-right">Items</th><th className="py-2 text-right">Total</th><th className="py-2"></th></tr></thead>
              <tbody>
                {histOrders.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No orders yet</td></tr>}
                {histOrders.map((o) => (
                  <tr key={o.id} className="border-b border-border" data-testid={`hist-order-${o.invoice_no}`}>
                    <td className="py-2 font-mono text-xs text-sky-700">{o.invoice_no}</td>
                    <td className="py-2 text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</td>
                    <td className="py-2 text-xs">{o.corporate_member_name || "—"}</td>
                    <td className="py-2 text-muted-foreground">{o.branch_name}</td>
                    <td className="py-2 text-right font-mono">{(o.lines || []).length}</td>
                    <td className="py-2 text-right font-mono">{fmtGHS(o.total)}</td>
                    <td className="py-2 text-right"><Button size="sm" variant="outline" data-testid={`hist-print-${o.invoice_no}`} onClick={() => printInvoice(o)}><Printer className="h-3.5 w-3.5 mr-1" /> PDF</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
